import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';

const enconOrange = '#ee7203';

/**
 * Clean Up Categories — Settings > Pricing > (supplier).
 * Lists every category of the supplier with how many products it holds and how many have no stock code, flags the ones that
 * look wrong (empty, only products left over from a PDF import with no stock code, or a name that is really text from a PDF
 * such as "Dear Mr Richardson," or "Uncategorised (PDF Import)"), ticks those by default, and deletes the ticked ones together
 * with their products. Nothing is deleted until the user confirms.
 */
export default function CleanUpCategoriesModal({ supplier, open, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get(`${API}/pricing/suppliers/${supplier.id}/category-health`)
      .then((res) => {
        if (!alive) return;
        const list = res.data?.categories || [];
        setCats(list);
        setPicked(new Set(list.filter((c) => c.suggest_delete).map((c) => c.id)));
      })
      .catch(() => { if (alive) toast.error('Failed to load categories'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, supplier.id]);

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const chosen = cats.filter((c) => picked.has(c.id));
  const productTotal = chosen.reduce((n, c) => n + c.product_count, 0);

  const doDelete = async () => {
    if (chosen.length === 0) return;
    if (!window.confirm(`Delete ${chosen.length} categor${chosen.length === 1 ? 'y' : 'ies'} and their ${productTotal} product${productTotal === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await api.post(`${API}/pricing/categories/bulk-delete`, { category_ids: chosen.map((c) => c.id) });
      toast.success(`Deleted ${res.data.deleted_categories} categor${res.data.deleted_categories === 1 ? 'y' : 'ies'} and ${res.data.deleted_products} product${res.data.deleted_products === 1 ? '' : 's'}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !deleting) onClose(); }}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pricing-cleanup-dialog">
        <DialogHeader><DialogTitle>Clean Up Categories — {supplier.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">
          Categories that look wrong are ticked for you: empty ones, ones holding only unlinked products left over from a PDF import,
          and ones named after text from a PDF (e.g. "Dear Mr Richardson," or "Uncategorised"). Untick anything you want to keep.
          Deleting a category also deletes the products in it.
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
        ) : cats.length === 0 ? (
          <p className="text-center text-gray-400 py-8">This supplier has no categories.</p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex gap-3 text-xs">
              <button type="button" className="text-orange-600 font-semibold hover:underline" onClick={() => setPicked(new Set(cats.filter((c) => c.suggest_delete).map((c) => c.id)))}>Tick suggested</button>
              <button type="button" className="text-gray-500 hover:underline" onClick={() => setPicked(new Set())}>Untick all</button>
            </div>
            {cats.map((c) => (
              <label key={c.id} className={`flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer ${picked.has(c.id) ? 'border-red-300 bg-red-50/40' : 'border-gray-200 hover:border-orange-300'}`} data-testid={`pricing-cleanup-row-${c.id}`}>
                <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} className="mt-1 w-4 h-4 accent-red-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{c.name}</span>
                    <span className="text-xs text-gray-400">{c.product_count} product{c.product_count === 1 ? '' : 's'}{c.unlinked_count > 0 ? ` · ${c.unlinked_count} without a stock code` : ''}</span>
                    {c.is_active && <span className="text-[10px] font-bold uppercase tracking-wide text-green-600">Active</span>}
                  </div>
                  {c.reasons.length > 0 && <div className="text-[11px] text-red-600">{c.reasons.join(' · ')}</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button className="text-white bg-red-600 hover:bg-red-700" onClick={doDelete} disabled={deleting || chosen.length === 0} data-testid="pricing-cleanup-delete-btn">
            {deleting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-1" /> Delete {chosen.length} categor{chosen.length === 1 ? 'y' : 'ies'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
